import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PlaytestEventLogProvider } from './playtest-event-log-context.js';
import { PlaytestsListPane } from './playtests-list-page.js';
import { PlaytestSessionsPane } from './playtest-sessions-page.js';
import { SessionDetailPane } from './session-detail-page.js';

// Two-column layout for /g/:gameShortId/playtests/* with the session
// detail as a right-side slide-over. Routing still drives state:
// /playtests/<id> shows list + sessions, /playtests/<id>/sessions/<sid>
// pops the slide-over open. PlaytestEventLogProvider owns the single
// supabase realtime channel for the active playtest so the panes don't
// race on the same channel name.
export function PlaytestsSectionPage(): JSX.Element {
  const params = useParams<{
    studioShortId: string;
    gameShortId: string;
    playtestId?: string;
    sessionId?: string;
  }>();
  const navigate = useNavigate();
  const playtestId = params.playtestId ?? null;
  const sessionId = params.sessionId ?? null;
  const detailOpen = sessionId !== null;

  const closeDetail = (): void => {
    if (playtestId === null) return;
    navigate(
      `/s/${params.studioShortId}/g/${params.gameShortId}/playtests/${playtestId}`,
    );
  };

  useEffect(() => {
    if (!detailOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeDetail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // closeDetail is stable enough for this scope; deps mirror what it reads.
  }, [detailOpen, params.studioShortId, params.gameShortId, playtestId]);

  const goToList = (): void => {
    navigate(`/s/${params.studioShortId}/g/${params.gameShortId}/playtests`);
  };

  // Mobile (<md) shows one pane at a time, picked by the URL: the list
  // when nothing is selected, otherwise the sessions for the selected
  // playtest. Desktop keeps both panes side-by-side. We toggle with
  // `contents`/`hidden` so the pane stays a direct grid child without
  // an extra layout div on desktop.
  const listVisibility = playtestId === null ? 'contents' : 'hidden md:contents';
  const sessionsVisibility = playtestId === null ? 'hidden md:contents' : 'contents';

  return (
    <PlaytestEventLogProvider playtestId={playtestId}>
      <div className="relative grid h-full min-h-0 grid-cols-1 overflow-hidden md:grid-cols-[340px_minmax(0,1fr)]">
        <div className={listVisibility}>
          <PlaytestsListPane selectedPlaytestId={playtestId} />
        </div>
        <div className={sessionsVisibility}>
          <PlaytestSessionsPane
            playtestId={playtestId}
            selectedSessionId={sessionId}
            onBack={goToList}
          />
        </div>

        <div
          aria-hidden={!detailOpen}
          onClick={closeDetail}
          className={
            detailOpen
              ? 'absolute inset-0 z-10 bg-black/40 transition-opacity duration-200 opacity-100'
              : 'pointer-events-none absolute inset-0 z-10 bg-black/40 transition-opacity duration-200 opacity-0'
          }
        />

        <aside
          role="dialog"
          aria-label="Session details"
          aria-hidden={!detailOpen}
          className={
            detailOpen
              ? 'absolute inset-y-0 right-0 z-20 flex w-full max-w-[640px] flex-col border-l border-border bg-surface shadow-2xl transition-transform duration-200 ease-out translate-x-0'
              : 'pointer-events-none absolute inset-y-0 right-0 z-20 flex w-full max-w-[640px] flex-col border-l border-border bg-surface shadow-2xl transition-transform duration-200 ease-out translate-x-full'
          }
        >
          <SessionDetailPane sessionId={sessionId} onClose={closeDetail} />
        </aside>
      </div>
    </PlaytestEventLogProvider>
  );
}
